// Greedy first-fit lane assignment for bars that would otherwise overlap.
//
// Bars cover whole day cells (see bar_geometry.js), so two bookings that share
// ANY day collide on screen even when their real timestamps do not. A lane is
// therefore free only when its last booking ended on a strictly earlier DAY —
// with exactly one exception.
//
// The exception is a handover: one rental returns in the morning and the next
// collects the same afternoon, on the SAME vehicle. There bar_geometry shortens
// the outgoing box so the two tile the shared column, so they genuinely do fit
// in one lane. markTurnarounds is what identifies those pairs, and passing its
// flags in here is deliberate: the shortening and the lane rule must agree on
// what "may share a lane" means. When they were two independent predicates they
// drifted — the month view mixes every vehicle into one row, so a pair from two
// DIFFERENT vehicles satisfied the timestamp test, shared a lane, and then had
// neither box shortened, painting one bar on top of the other.

function dayOf(isoString) {
  return String(isoString).split("T")[0];
}

/**
 * @param {Array<{id: string|number, start: string, end: string}>} events
 * @param {{maxLanes?: number, turnarounds?: Map<string|number, {tilesWithNext: string|number|null}>}} [options]
 *   `turnarounds` comes from markTurnarounds, grouped per resource by the
 *   caller. Omit it and every same-day pair stacks, which is the safe default.
 * @returns {{lanes: Array<Array<object>>, overflow: Array<object>}}
 */
export function packLanes(events, { maxLanes = 3, turnarounds = null } = {}) {
  const sorted = [...events].sort((a, b) => String(a.start).localeCompare(String(b.start)));

  const lanes = [];
  const laneLast = []; // the last EVENT in each lane, not just its end string
  const overflow = [];

  const tiles = (previous, event) =>
    !!turnarounds && turnarounds.get(previous.id)?.tilesWithNext === event.id;

  sorted.forEach((event) => {
    const laneIdx = laneLast.findIndex(
      (previous) => dayOf(previous.end) < dayOf(event.start) || tiles(previous, event)
    );

    if (laneIdx !== -1) {
      lanes[laneIdx].push(event);
      laneLast[laneIdx] = event;
    } else if (lanes.length < maxLanes) {
      lanes.push([event]);
      laneLast.push(event);
    } else {
      overflow.push(event);
    }
  });

  return { lanes, overflow };
}
